import { FieldKey } from '../../context/field-keys.js';
import { defaultRound } from '../../shared/round.js';
/**
 * Sale aggregate calcs, ported from `sale-auto-calc-listeners.ts`:
 *
 *   PGI  = passingRentAmountYearly + vacantEstimatedRentalAmount   (both null/0 -> null)
 *   GIA  = salePriceActual + buyerCost                              (both null/0 -> null)
 *   VPVR = vacantPossessionValueAmount / netRentableAreaInSqM       (either 0/null -> null)
 *
 * PGI/GIA "both null OR 0 -> null, else sum with null coerced to 0" matches the source exactly.
 * VPVR needs the NRA converted to square metres first; that UOM conversion is host-side, so VPVR is
 * exposed as a pure kernel taking the already-converted area (no field rule here).
 */
/** Sum of two amounts where the whole result is null iff both are null/0. Used by PGI and GIA. */
function nullableSum(a, b) {
  const aEmpty = a == null || a === 0;
  const bEmpty = b == null || b === 0;
  if (aEmpty && bEmpty) {
    return null;
  }
  return (a ?? 0) + (b ?? 0);
}
/**
 * Potential Gross Income kernel = passing rent (yearly) + vacant estimated rental amount.
 * Returns null iff BOTH inputs are null/0; otherwise the sum with null coerced to 0 (uses `?? 0`,
 * so a literal 0 input is kept, not treated as empty in the sum). Byte-identical to the host
 * `calculatePotentialGrossIncome` value+null logic (the host keeps format/round/UOM around it).
 */
export function potentialGrossIncome(passingRentAmountYearly, vacantEstimatedRentalAmount) {
  return nullableSum(passingRentAmountYearly, vacantEstimatedRentalAmount);
}
/**
 * Gross Investment Amount kernel = actual sale price + buyer cost. Same null/sum semantics as
 * {@link potentialGrossIncome}; byte-identical to the host `calculateGrossInvestmentAmount` value logic.
 */
export function grossInvestmentAmount(salePriceActual, buyerCost) {
  return nullableSum(salePriceActual, buyerCost);
}
/** Potential Gross Income = passing rent (yearly) + vacant estimated rental amount. */
export const potentialGrossIncomeRule = {
  id: 'sale.potential-gross-income.from-passing-and-vacant',
  inputs: [FieldKey.PassingRentAmountYearly, FieldKey.VacantEstimatedRentalAmount],
  outputs: [FieldKey.PotentialGrossIncome],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const sum = potentialGrossIncome(ctx.get(FieldKey.PassingRentAmountYearly), ctx.get(FieldKey.VacantEstimatedRentalAmount));
    ctx.set(FieldKey.PotentialGrossIncome, sum == null ? null : round(sum, FieldKey.PotentialGrossIncome));
  }
};
/** Gross Investment Amount = actual sale price + buyer cost. */
export const grossInvestmentAmountRule = {
  id: 'sale.gross-investment-amount.from-price-and-buyer-cost',
  inputs: [FieldKey.SalePriceActual, FieldKey.BuyerCost],
  outputs: [FieldKey.GrossInvestmentAmount],
  formulaVersion: '1.0.0',
  run: (ctx, deps) => {
    const round = deps?.round ?? defaultRound;
    const sum = grossInvestmentAmount(ctx.get(FieldKey.SalePriceActual), ctx.get(FieldKey.BuyerCost));
    ctx.set(FieldKey.GrossInvestmentAmount, sum == null ? null : round(sum, FieldKey.GrossInvestmentAmount));
  }
};
/**
 * Vacant Possession Value Rate = amount / NRA(in m²). Pure kernel — the host converts NRA to square
 * metres (via the uom lib) before calling, then applies its own rounding/UOM. Null when either is 0/null.
 */
export function calculateVacantPossessionValueRate(vacantPossessionValueAmount, netRentableAreaInSqM) {
  if (vacantPossessionValueAmount == null || vacantPossessionValueAmount === 0 || !netRentableAreaInSqM) {
    return null;
  }
  return vacantPossessionValueAmount / netRentableAreaInSqM;
}
export const saleAggregateRules = [potentialGrossIncomeRule, grossInvestmentAmountRule];
